import type { RegisterVisionUrlPayload } from '@/core/api/interfaces/registerVisionUrlPayload.interface'
import type { GatewayConfig } from '@/core/api/libraries.api'
import { registerVisionUrl } from '@/core/api/registerVisionUrl.api'

import { VISION_URL_REGISTRATION_RETRY_DELAYS_MS } from '../const/visionUrlRegistrationRetryDelaysMs.const'
import { collectVisionLocationMetadata } from '../mappers/collectVisionLocationMetadata'

import { isGatewayConfigAvailable } from './isGatewayConfigAvailable'
import { isTransientGatewayError } from './isTransientGatewayError'
import { resolveVisionIpAddress } from './resolveVisionIpAddress'
import { hasVisionUrlRegistrationGuard, markVisionUrlRegistered } from './visionUrlRegistrationGuard'

let hasStartedRegistration = false
let retryDelaysMs: readonly number[] = VISION_URL_REGISTRATION_RETRY_DELAYS_MS

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

export const __resetVisionUrlRegistrationForTests = (): void => {
  hasStartedRegistration = false
  retryDelaysMs = VISION_URL_REGISTRATION_RETRY_DELAYS_MS
}

export const __setVisionUrlRegistrationRetryDelaysForTests = (delays: readonly number[]): void => {
  retryDelaysMs = delays
}

const buildPayload = async (config: GatewayConfig): Promise<RegisterVisionUrlPayload | null> => {
  const metadata = collectVisionLocationMetadata()
  if (!metadata) {
    return null
  }

  const ipAddress = await resolveVisionIpAddress(config, metadata.hostname)
  if (!ipAddress) {
    return metadata
  }

  return { ...metadata, ipAddress }
}

const attemptRegistration = async (config: GatewayConfig): Promise<void> => {
  const payload = await buildPayload(config)
  if (!payload) {
    return
  }

  await registerVisionUrl(config, payload)
  markVisionUrlRegistered()
}

export const registerVisionUrlIfNeeded = async (config: GatewayConfig): Promise<void> => {
  if (hasStartedRegistration) {
    return
  }

  if (!isGatewayConfigAvailable(config) || hasVisionUrlRegistrationGuard()) {
    return
  }

  hasStartedRegistration = true

  const maxAttempts = retryDelaysMs.length + 1

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      await attemptRegistration(config)
      return
    } catch (error) {
      const canRetry = attempt < maxAttempts - 1 && isTransientGatewayError(error)
      if (!canRetry) {
        console.warn('Vision URL registration failed', error)
        return
      }

      const delayMs = retryDelaysMs[attempt]
      if (delayMs) {
        await delay(delayMs)
      }
    }
  }
}
